import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';

const LS_KEY = 'rumio_exit_intent_dismissed';

export default function ExitIntentPopup() {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const config = data?.settings?.upsell_config?.exit_intent;

  useEffect(() => {
    if (!config?.enabled) return;
    if (localStorage.getItem(LS_KEY)) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        setIsOpen(true);
        if (config.show_once_per_session) {
          sessionStorage.setItem(LS_KEY, '1');
        }
        // Remove listener after triggering once
        document.removeEventListener('mouseout', handleMouseLeave);
      }
    };

    // Don't trigger on mobile or if already shown this session
    if (window.matchMedia('(pointer: fine)').matches && !sessionStorage.getItem(LS_KEY)) {
      // Delay registering the listener so it doesn't trigger immediately
      const timer = setTimeout(() => {
        document.addEventListener('mouseout', handleMouseLeave);
      }, 5000);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mouseout', handleMouseLeave);
      };
    }
  }, [config]);

  // Focus trap and keyboard handling when open
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Move focus into dialog
    const timer = setTimeout(() => {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [isOpen]);

  function handleDismiss() {
    setIsOpen(false);
    localStorage.setItem(LS_KEY, '1');
  }

  if (!config?.enabled) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('exitIntent.ariaLabel')}
        >
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            className="relative z-10 w-full max-w-md rounded-xl bg-surface shadow-xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <button
              onClick={handleDismiss}
              className="absolute right-3 top-3 z-10 rounded-lg p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-soft"
              aria-label={t('exitIntent.close')}
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-6 pt-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sage-100">
                <Tag className="h-6 w-6 text-sage-600" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-ink mb-2">
                {config.headline || t('exitIntent.defaultHeadline')}
              </h2>
              <p className="text-ink-soft text-sm mb-6">
                {config.body || t('exitIntent.defaultBody')}
              </p>
              {config.discount_code && (
                <div className="rounded-lg bg-success-50 border border-success-200 p-4 mb-6">
                  <p className="text-sm text-success-800 mb-1">{t('exitIntent.useCode')}</p>
                  <p className="text-2xl font-bold text-success-900 font-mono tracking-wider">
                    {config.discount_code}
                  </p>
                </div>
              )}
              <button
                onClick={handleDismiss}
                className="w-full rounded-lg bg-sage-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sage-800 transition-colors"
              >
                {t('exitIntent.continueShopping')}
              </button>
              <button
                onClick={handleDismiss}
                className="mt-3 text-sm text-ink-muted hover:text-ink-soft"
              >
                {t('exitIntent.noThanks')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
